// Leaflet route map. The DTO ships routes as a GeoJSON FeatureCollection,
// so we hand it straight to L.geoJSON. Leaflet is imperative, so the map
// lives in a ref and is (re)built in an effect when the result changes.
// Logic ported from the v2 SPA's map.ts.

import { useEffect, useRef } from "react";
import L from "leaflet";

import { type Vars, useT } from "../lib/i18n";
import type { OptimizeResponseDTO, RouteFeatureDTO, UnroutedPoint } from "../types";

// CARTO now watermarks unauthenticated raster tiles, so the key is appended
// when present and the map simply renders watermarked without it. The value
// is baked into the browser bundle like any VITE_ var — that is inherent to
// client-side tiles, so restrict the key by domain in the CARTO dashboard
// rather than treating it as a secret.
//
// NOTE: CARTO is retiring these raster basemaps in favour of vector styles
// via MapLibre. Worth revisiting before they go away.
const CARTO_KEY = import.meta.env.VITE_CARTO_KEY as string | undefined;
const CARTO_TILE_URL =
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" +
  (CARTO_KEY ? `?key=${CARTO_KEY}` : "");


function routePopup(
  props: RouteFeatureDTO["properties"],
  t: (key: "map.truck" | "map.stopsUnit", vars?: Vars) => string,
): string {
  const head =
    props.kind === "own_truck"
      ? `${t("map.truck")} ${props.truck_label ?? "?"} — ${props.route_id}`
      : props.route_id;
  return (
    `<strong>${head}</strong><br>` +
    `${props.n_stops} ${t("map.stopsUnit")} · ${props.miles.toFixed(1)} mi` +
    (props.cost_usd ? `<br>$${props.cost_usd.toFixed(0)}` : "")
  );
}

// Only the depot and the route lines are drawn, so the dispatch plan can feed
// its recalibrated routes too. `highlight` fades every other route, and
// `highlightStop` (a ZIP on that route) enlarges its marker. `onSelect` is
// called when a route or a stop marker is clicked.
const NONE: UnroutedPoint[] = [];

export function RouteMap({
  result,
  highlight = null,
  highlightStop = null,
  onSelect,
  unrouted = NONE,
}: {
  result: Pick<OptimizeResponseDTO, "depot" | "routes">;
  highlight?: string | null;
  highlightStop?: string | null;
  onSelect?: (routeId: string, zip?: string) => void;
  // A plan's orders on no trip, pinned plainly so they can be grouped by place.
  unrouted?: UnroutedPoint[];
}) {
  const t = useT();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  // Kept in refs so a new callback or a new selection doesn't refit the map
  // the dispatcher may have zoomed.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const fittedTo = useRef<unknown>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!mapRef.current) {
      mapRef.current = L.map(containerRef.current, { center: [33.77, -118.19], zoom: 10 });
      L.tileLayer(CARTO_TILE_URL, {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> ' +
          '&copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: "abcd",
        maxZoom: 19,
      }).addTo(mapRef.current);
    }
    const map = mapRef.current;

    const layers: L.Layer[] = [];

    // Depot.
    const depot = result.depot;
    layers.push(
      L.marker([depot.lat, depot.lon], {
        icon: L.divIcon({
          className: "depot-marker",
          html: '<div class="depot-square"></div>',
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        }),
      })
        .bindPopup(`<strong>${t("map.depot")}</strong><br>${depot.zip}`)
        .addTo(map),
    );

    // Routes, longest first: a short route can share every leg with a longer
    // one (T2 out to one stop and back, along T3's last leg) and would vanish
    // beneath it.
    const longestFirst = {
      ...result.routes,
      features: [...result.routes.features].sort(
        (a, b) => b.properties.miles - a.properties.miles,
      ),
    };
    const routes = L.geoJSON(longestFirst as never, {
      style: (feature) => {
        const props = (feature as RouteFeatureDTO).properties;
        const faded = highlight !== null && props.route_id !== highlight;
        return {
          color: props.color,
          weight: props.route_id === highlight ? 6 : props.kind === "own_truck" ? 4 : 3,
          opacity: faded ? 0.25 : 0.9,
          dashArray: props.trip_index && props.trip_index > 0 ? "8 6" : undefined,
        };
      },
      onEachFeature: (feature, layer) => {
        const props = (feature as RouteFeatureDTO).properties;
        layer.bindPopup(routePopup(props, t));
        layer.on("click", () => onSelectRef.current?.(props.route_id));
      },
    }).addTo(map);
    layers.push(routes);

    // Numbered stop markers along each route. The line starts at the depot,
    // so stop i is point i + 1, whether or not the line drives back.
    const markers = L.layerGroup();
    for (const feature of result.routes.features) {
      const props = feature.properties;
      const coords = feature.geometry.coordinates;
      for (let i = 1; i < coords.length; i++) {
        const stop = props.stops[i - 1];
        if (!stop) continue;
        const [lon, lat] = coords[i];
        const selected = props.route_id === highlight && stop.zip === highlightStop;
        L.marker([lat, lon], {
          icon: L.divIcon({
            className: `stop-marker${selected ? " is-selected" : ""}`,
            html: `<span class="stop-num" style="border-color:${props.color};color:${props.color};">${stop.seq}</span>`,
            iconSize: [22, 22],
            iconAnchor: [11, 11],
          }),
          zIndexOffset: selected ? 1000 : 0,
        })
          .bindPopup(
            `<strong>${t("map.stop")} ${stop.seq} — ${stop.zip}</strong>` +
              (stop.eta_clock ? `<br>${t("map.eta")} ${stop.eta_clock}` : ""),
          )
          .on("click", () => onSelectRef.current?.(props.route_id, stop.zip))
          .addTo(markers);
      }
    }
    markers.addTo(map);
    layers.push(markers);

    const pins = L.featureGroup(
      unrouted.map((o) =>
        L.marker([o.lat, o.lon], {
          icon: L.divIcon({
            className: "unrouted-marker",
            html: '<span class="unrouted-pin"></span>',
            iconSize: [12, 12],
            iconAnchor: [6, 6],
          }),
        }).bindPopup(`<strong>${o.order_no} — ${o.zip}</strong><br>${t("map.unrouted")}`),
      ),
    ).addTo(map);
    layers.push(pins);

    const bounds = routes.getBounds();
    if (unrouted.length > 0) bounds.extend(pins.getBounds());
    if (fittedTo.current !== result && bounds.isValid()) {
      map.fitBounds(bounds.pad(0.1));
      fittedTo.current = result;
    }
    // Leaflet needs a size recalc after the container settles.
    setTimeout(() => map.invalidateSize(), 0);

    return () => {
      for (const layer of layers) layer.remove();
    };
  }, [result, highlight, highlightStop, unrouted, t]);

  return <div ref={containerRef} className="map-frame" />;
}
